/* eslint-disable camelcase */
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Checkbox,
  HStack,
  Icon,
  Stack,
  Text,
  useDisclosure,
  Input,
  VStack,
  SimpleGrid
} from '@chakra-ui/react';
import { memo, Fragment } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

// import { useInitiatives } from '@services/query-services/initiatives.query-service';
import { useContentTypeOptions } from '@util/hooks/feed.hooks';
import { dateRangeMenuItems } from '@util/constants';
import { useUserStore } from '@store/app.store';

import FeedRangepicker, { SelectedDateRange } from '../feed/feed-rangepicker.component';

/**
 * Modal-friendly version of FeedFilterAccordionStackComponent
 * Uses react-hook-form context for form state management
 */
function CardSearchFilterAccordionStack({ startDate, endDate, setStartDate, setEndDate }) {
  const { control, watch, setValue } = useFormContext();
  const user = useUserStore((state) => state?.user);
  const tenant = useUserStore((state) => state.tenant);
  const isGuest = user?.isGuest;

  // const { data: focuses } = useInitiatives({ type: 'Focus' }, { enabled: !!(user && !isGuest) });
  const contentTypeFilters = useContentTypeOptions();

  // Watch necessary form values
  const filters = watch();
  const { dateRange, contentTypeSubSources, participantName, participantEmail } = filters;

  const disclosure = useDisclosure();
  const rangePickerDisclosure = {
    ...disclosure,
    onClose: (confirm = false) => {
      if (!confirm) {
        setValue('dateRange', 'All Dates');
      }
      disclosure.onClose();
    }
  };

  // const handleFocusChecked = (e, focusId) => {
  //   const checked = e?.target?.checked;
  //   const newFocusFilters = checked
  //     ? [...filters.focuses, focusId]
  //     : filters.focuses.filter((f) => f !== focusId);
  //   setValue('focuses', newFocusFilters);
  // };

  const handleContentTypeChecked = (e, contentType) => {
    const { sub_sources } = contentType;
    const checked = e?.target?.checked;
    const newSubSourcesFilter = checked
      ? [...contentTypeSubSources, ...sub_sources]
      : contentTypeSubSources.filter((subSource) => !sub_sources.includes(subSource));
    setValue('contentTypeSubSources', newSubSourcesFilter);
  };

  const handleDateRangeSelect = (dateRange) => {
    setValue('dateRange', dateRange.name);
    if (dateRange.createdAtFrom) {
      setStartDate(dateRange.createdAtFrom.getTime());
    }
  };

  const selectedContentTypesCount = contentTypeFilters.filter((contentType) =>
    contentType.sub_sources.every((subSource) => contentTypeSubSources?.includes(subSource))
  ).length;

  // const selectedCategoriesCount = filters?.focuses?.length || 0;

  const selectedPeopleCount = [participantName, participantEmail].filter(
    (field) => field && field.trim() !== ''
  ).length;

  const selectedCompaniesCount = [
    filters.participantCompanyName,
    filters.participantCompanyDomain
  ].filter((field) => field && field.trim() !== '').length;

  // const selectedSavedArchivedCount = [filters.savedFilter, filters.archivedFilter].filter(
  //   Boolean
  // ).length;

  return (
    <Fragment>
      <SimpleGrid
        columns={{ base: 1, lg: 2 }}
        spacing={4}
        alignItems='start'
      >
        {/* Left Column */}
        <Box
          bg='white'
          border='1px solid'
          borderColor='gray.200'
          borderRadius='lg'
          overflow='hidden'
          alignSelf='start'
        >
          <Accordion allowMultiple>
            {/* Date Range */}
            <AccordionItem border='none'>
              <AccordionButton
                py={4}
                px={6}
                _hover={{ bg: 'gray.50' }}
                borderBottom='1px solid'
                borderColor='gray.200'
              >
                <Box
                  flex='1'
                  textAlign='left'
                >
                  <Text
                    fontSize='md'
                    fontWeight='500'
                    color='gray.900'
                  >
                    Date Range
                    {dateRange && dateRange !== 'All Dates' && (
                      <Text
                        as='span'
                        color='surface.outline'
                        fontSize='sm'
                        ml={1}
                        data-testid='date-range-selection'
                      >
                        ({dateRange})
                      </Text>
                    )}
                  </Text>
                </Box>
                <AccordionIcon color='gray.500' />
              </AccordionButton>
              <AccordionPanel
                pb={4}
                px={6}
              >
                <Stack spacing={3}>
                  {dateRangeMenuItems.map((dateRange) => (
                    <HStack
                      key={dateRange.name}
                      spacing={3}
                      py={2}
                      cursor='pointer'
                      onClick={() => handleDateRangeSelect(dateRange)}
                      _hover={{ bg: 'gray.50' }}
                      borderRadius='md'
                      px={2}
                    >
                      <Text
                        fontSize='sm'
                        color='gray.700'
                        flex='1'
                      >
                        {dateRange.name}
                      </Text>
                      <Checkbox
                        isChecked={filters.dateRange === dateRange.name}
                        onChange={() => handleDateRangeSelect(dateRange)}
                        colorScheme='blue'
                        size='md'
                      />
                    </HStack>
                  ))}
                  <HStack
                    spacing={3}
                    py={2}
                    cursor='pointer'
                    _hover={{ bg: 'gray.50' }}
                    borderRadius='md'
                    px={2}
                    onClick={() => {
                      setValue('dateRange', 'Custom');
                      rangePickerDisclosure.onOpen();
                    }}
                  >
                    <Text
                      fontSize='sm'
                      color='gray.700'
                      flex='1'
                    >
                      Custom...
                    </Text>
                    <Checkbox
                      isChecked={filters.dateRange === 'Custom'}
                      onChange={() => {
                        setValue('dateRange', 'Custom');
                        rangePickerDisclosure.onOpen();
                      }}
                      colorScheme='blue'
                      size='md'
                    />
                  </HStack>
                  {filters.dateRange === 'Custom' && (
                    <Box mt={4}>
                      <SelectedDateRange
                        startDate={startDate}
                        endDate={endDate}
                        onClick={rangePickerDisclosure.onOpen}
                      />
                    </Box>
                  )}
                </Stack>
              </AccordionPanel>
            </AccordionItem>
            {/* Content Types */}
            {!isGuest && (
              <AccordionItem border='none'>
                <AccordionButton
                  py={4}
                  px={6}
                  _hover={{ bg: 'gray.50' }}
                  borderBottom='1px solid'
                  borderColor='gray.200'
                >
                  <Box
                    flex='1'
                    textAlign='left'
                  >
                    <Text
                      fontSize='md'
                      fontWeight='500'
                      color='gray.900'
                    >
                      Content Types
                      {selectedContentTypesCount > 0 && (
                        <Text
                          as='span'
                          color='surface.outline'
                          fontSize='sm'
                          ml={1}
                          data-testid='content-types-count'
                        >
                          ({selectedContentTypesCount})
                        </Text>
                      )}
                    </Text>
                  </Box>
                  <AccordionIcon color='gray.500' />
                </AccordionButton>
                <AccordionPanel
                  pb={4}
                  px={6}
                >
                  <Stack spacing={3}>
                    {contentTypeFilters.map((contentType) => (
                      <HStack
                        key={contentType?.title}
                        spacing={3}
                        py={2}
                      >
                        <Icon
                          as={contentType.icon}
                          color='gray.500'
                          boxSize={5}
                        />
                        <Text
                          fontSize='sm'
                          color='gray.700'
                          flex='1'
                        >
                          {contentType?.title}
                        </Text>
                        <Checkbox
                          isChecked={contentType.sub_sources.every((subSource) =>
                            contentTypeSubSources?.includes(subSource)
                          )}
                          onChange={(e) => handleContentTypeChecked(e, contentType)}
                          colorScheme='blue'
                          size='md'
                        />
                      </HStack>
                    ))}
                  </Stack>
                </AccordionPanel>
              </AccordionItem>
            )}

            {/* Categories
            {!isGuest && (
              <AccordionItem border='none'>
                <AccordionButton
                  py={4}
                  px={6}
                  _hover={{ bg: 'gray.50' }}
                  borderBottom='1px solid'
                  borderColor='gray.200'
                >
                  <Box
                    flex='1'
                    textAlign='left'
                  >
                    <Text
                      fontSize='md'
                      fontWeight='500'
                      color='gray.900'
                    >
                      Categories
                      {selectedCategoriesCount > 0 && (
                        <Text
                          as='span'
                          color='surface.outline'
                          fontSize='sm'
                          ml={1}
                          data-testid='categories-count'
                        >
                          ({selectedCategoriesCount})
                        </Text>
                      )}
                    </Text>
                  </Box>
                  <AccordionIcon color='gray.500' />
                </AccordionButton>
                <AccordionPanel
                  pb={4}
                  px={6}
                >
                  <Stack spacing={3}>
                    {(focuses ?? []).map((focus) => (
                      <HStack
                        key={focus?.id}
                        spacing={3}
                        py={2}
                      >
                        <Text
                          fontSize='sm'
                          color='gray.700'
                          flex='1'
                        >
                          {focus?.title}
                        </Text>
                        <Checkbox
                          isChecked={filters?.focuses?.includes(focus?.id)}
                          onChange={(e) => handleFocusChecked(e, focus?.id)}
                          colorScheme='blue'
                          size='md'
                          data-testid='focus-checkbox'
                        />
                      </HStack>
                    ))}
                  </Stack>
                </AccordionPanel>
              </AccordionItem>
            )} */}
          </Accordion>
        </Box>

        {/* Right Column */}
        <Box
          bg='white'
          border='1px solid'
          borderColor='gray.200'
          borderRadius='lg'
          overflow='hidden'
          alignSelf='start'
        >
          <Accordion allowMultiple>
            {/* People */}
            {!isGuest && (
              <AccordionItem border='none'>
                <AccordionButton
                  py={4}
                  px={6}
                  _hover={{ bg: 'gray.50' }}
                  borderBottom='1px solid'
                  borderColor='gray.200'
                >
                  <Box
                    flex='1'
                    textAlign='left'
                  >
                    <Text
                      fontSize='md'
                      fontWeight='500'
                      color='gray.900'
                    >
                      People
                      {selectedPeopleCount > 0 && (
                        <Text
                          as='span'
                          color='surface.outline'
                          fontSize='sm'
                          ml={1}
                          data-testid='people-count'
                        >
                          ({selectedPeopleCount})
                        </Text>
                      )}
                    </Text>
                  </Box>
                  <AccordionIcon color='gray.500' />
                </AccordionButton>
                <AccordionPanel
                  pt={4}
                  pb={4}
                  px={6}
                >
                  <VStack
                    spacing={4}
                    align='stretch'
                  >
                    <Box>
                      <Text
                        size='xs'
                        fontWeight={300}
                        fontSize={14}
                      >
                        Participant Name
                      </Text>
                      <Controller
                        name='participantName'
                        control={control}
                        render={({ field }) => (
                          <Input
                            size='sm'
                            w='full'
                            fontSize='sm'
                            h={10}
                            p={5}
                            {...field}
                            borderRadius='md'
                            placeholder='Enter a participant name'
                            _placeholder={{
                              color: 'surface.outline',
                              p: 0.5,
                              ml: 1,
                              textStyle: 'body-500-sm'
                            }}
                          />
                        )}
                      />
                    </Box>
                    <Box>
                      <Text
                        size='xs'
                        fontWeight={300}
                        fontSize={14}
                      >
                        Participant Email
                      </Text>
                      <Controller
                        name='participantEmail'
                        control={control}
                        render={({ field }) => (
                          <Input
                            size='sm'
                            w='full'
                            fontSize='sm'
                            h={10}
                            p={5}
                            {...field}
                            borderRadius='md'
                            placeholder='Enter a participant email'
                            _placeholder={{
                              color: 'surface.outline',
                              p: 0.5,
                              ml: 1,
                              textStyle: 'body-500-sm'
                            }}
                          />
                        )}
                      />
                    </Box>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            )}

            {/* Companies */}
            {!isGuest && (
              <AccordionItem border='none'>
                <AccordionButton
                  py={4}
                  px={6}
                  _hover={{ bg: 'gray.50' }}
                  borderBottom='1px solid'
                  borderColor='gray.200'
                >
                  <Box
                    flex='1'
                    textAlign='left'
                  >
                    <Text
                      fontSize='md'
                      fontWeight='500'
                      color='gray.900'
                    >
                      Companies
                      {selectedCompaniesCount > 0 && (
                        <Text
                          as='span'
                          color='surface.outline'
                          fontSize='sm'
                          ml={1}
                          data-testid='companies-count'
                        >
                          ({selectedCompaniesCount})
                        </Text>
                      )}
                    </Text>
                  </Box>
                  <AccordionIcon color='gray.500' />
                </AccordionButton>
                <AccordionPanel
                  pb={4}
                  px={6}
                >
                  <VStack
                    spacing={4}
                    align='stretch'
                  >
                    <Box>
                      <Text
                        size='xs'
                        fontWeight={300}
                        fontSize={14}
                      >
                        Company Name
                      </Text>
                      <Controller
                        name='participantCompanyName'
                        control={control}
                        render={({ field }) => (
                          <Input
                            size='sm'
                            w='full'
                            fontSize='sm'
                            h={10}
                            p={5}
                            {...field}
                            borderRadius='md'
                            placeholder='Enter a company name'
                            _placeholder={{
                              color: 'surface.outline',
                              p: 0.5,
                              ml: 1,
                              textStyle: 'body-500-sm'
                            }}
                          />
                        )}
                      />
                    </Box>
                    <Box>
                      <Text
                        size='xs'
                        fontWeight={300}
                        fontSize={14}
                      >
                        Company Domain
                      </Text>
                      <Controller
                        name='participantCompanyDomain'
                        control={control}
                        render={({ field }) => (
                          <Input
                            size='sm'
                            w='full'
                            fontSize='sm'
                            h={10}
                            p={5}
                            {...field}
                            borderRadius='md'
                            placeholder={`Enter a company domain (e.g. ${
                              tenant.domains?.length ? `"${tenant.domains[0]}"` : 'gmail.com'
                            })`}
                            _placeholder={{
                              color: 'surface.outline',
                              p: 0.5,
                              ml: 1,
                              textStyle: 'body-500-sm'
                            }}
                          />
                        )}
                      />
                    </Box>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            )}

            {/* Saved & Archived */}
            {/* {!isGuest && (
              <AccordionItem border='none'>
                <AccordionButton
                  py={4}
                  px={6}
                  _hover={{ bg: 'gray.50' }}
                >
                  <Box
                    flex='1'
                    textAlign='left'
                  >
                    <Text
                      fontSize='md'
                      fontWeight='500'
                      color='gray.900'
                    >
                      Saved & Archived
                      {selectedSavedArchivedCount > 0 && (
                        <Text
                          as='span'
                          color='surface.outline'
                          fontSize='sm'
                          ml={1}
                          data-testid='saved-archived-count'
                        >
                          ({selectedSavedArchivedCount})
                        </Text>
                      )}
                    </Text>
                  </Box>
                  <AccordionIcon color='gray.500' />
                </AccordionButton>
                <AccordionPanel
                  pb={4}
                  px={6}
                >
                  <Stack spacing={3}>
                    <HStack
                      spacing={3}
                      py={2}
                    >
                      <Text
                        fontSize='sm'
                        color='gray.700'
                        flex='1'
                      >
                        Saved Summaries
                      </Text>
                      <Checkbox
                        isChecked={filters.savedFilter}
                        onChange={(e) => {
                          handleFilterChange('savedFilter', e.target.checked ? true : null);
                        }}
                        colorScheme='blue'
                        size='md'
                        data-testid='saved-filter-checkbox'
                      />
                    </HStack>
                    <HStack
                      spacing={3}
                      py={2}
                    >
                      <Text
                        fontSize='sm'
                        color='gray.700'
                        flex='1'
                      >
                        Archived Summaries
                      </Text>
                      <Checkbox
                        isChecked={filters.archivedFilter}
                        onChange={(e) => {
                          handleFilterChange('archivedFilter', e.target.checked ? true : null);
                        }}
                        colorScheme='blue'
                        size='md'
                        data-testid='archived-filter-checkbox'
                      />
                    </HStack>
                  </Stack>
                </AccordionPanel>
              </AccordionItem>
            )} */}
          </Accordion>
        </Box>
      </SimpleGrid>

      <FeedRangepicker
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        disclosure={rangePickerDisclosure}
      />
    </Fragment>
  );
}

export default memo(CardSearchFilterAccordionStack);
